import { createContext, useContext } from 'react';

/**
 * Interface defining the shape of the Device Preview Navigation context value.
 * Provides modal state, methods to open/close the Test Campaign modal,
 * and device preview feature availability.
 */
export interface DevicePreviewNavigationContextValue {
  /** Whether the Test Campaign modal is currently open */
  isTestCampaignModalOpen: boolean;
  /** Opens the Test Campaign modal */
  openTestCampaignModal: () => void;
  /** Closes the Test Campaign modal */
  closeTestCampaignModal: () => void;
  /** Whether device preview is available (feature flag + Fire TV + Feature Rotator) */
  isDevicePreviewAvailable: boolean;
}

/**
 * React Context for managing Test Campaign modal navigation.
 * Allows the modal to be triggered from multiple entry points across the component tree.
 */
export const DevicePreviewNavigationContext = createContext<
  DevicePreviewNavigationContextValue | undefined
>(undefined);

/**
 * Custom hook to access the Device Preview Navigation context.
 * @throws {Error} If used outside of a DevicePreviewNavigationProvider
 * @returns {DevicePreviewNavigationContextValue} The navigation context value
 */
export const useDevicePreviewNavigation = (): DevicePreviewNavigationContextValue => {
  const context = useContext(DevicePreviewNavigationContext);
  if (!context) {
    throw new Error(
      'useDevicePreviewNavigation must be used within a DevicePreviewNavigationProvider',
    );
  }
  return context;
};
